/**
 * AdvisorDecisionQuality Component
 * 
 * Shows authorization timing, success rates, and approval-to-completion metrics.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Clock, CheckCircle, XCircle, RotateCcw } from 'lucide-react';
import type { AdvisorIntelligenceStats } from '@/hooks/useAdvisorIntelligenceStats';

interface AdvisorDecisionQualityProps {
  stats: AdvisorIntelligenceStats | null | undefined;
  isSelfView: boolean;
}

export function AdvisorDecisionQuality({ stats, isSelfView }: AdvisorDecisionQualityProps) {
  if (!stats) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Decision Quality
          </CardTitle>
          <CardDescription>Not enough data yet. Continue processing tickets to build your profile.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const formatRate = (rate: number | null) => {
    if (rate == null) return '—';
    return `${Math.round(rate * 100)}%`;
  };

  const formatMinutes = (minutes: number | null) => {
    if (minutes == null) return '—';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const metrics = [
    {
      label: 'Avg. Authorization Time',
      value: formatMinutes(stats.authLatencyAvgMinutes),
      icon: Clock,
      description: 'Time from ticket creation to customer authorization',
      showProgress: false,
    },
    {
      label: 'Authorization Success',
      value: formatRate(stats.authSuccessRate),
      icon: CheckCircle,
      description: 'Tickets authorized within target timeframe',
      progress: stats.authSuccessRate != null ? stats.authSuccessRate * 100 : null,
      progressColor: 'bg-green-500',
    },
    {
      label: 'Approval → Completion',
      value: formatRate(stats.approvedToCompletedRate),
      icon: CheckCircle,
      description: 'Approved work that gets completed',
      progress: stats.approvedToCompletedRate != null ? stats.approvedToCompletedRate * 100 : null,
      progressColor: 'bg-blue-500',
    },
    {
      label: 'Declined Return Rate',
      value: formatRate(stats.declinedReturnRate),
      icon: RotateCcw,
      description: 'Previously declined work that returned as needed',
      progress: stats.declinedReturnRate != null ? stats.declinedReturnRate * 100 : null,
      progressColor: 'bg-amber-500',
      inverse: true, // Lower is better
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5" />
          Decision Quality
        </CardTitle>
        <CardDescription>
          How well authorization decisions translate to completed work
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {metrics.map((metric) => (
            <div key={metric.label} className="space-y-2 p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <metric.icon className="h-4 w-4 text-muted-foreground" />
                  {metric.label}
                </div>
                <span className="text-lg font-semibold">{metric.value}</span>
              </div>
              {metric.progress != null && (
                <Progress 
                  value={metric.inverse ? 100 - metric.progress : metric.progress} 
                  className="h-2"
                />
              )}
              <p className="text-xs text-muted-foreground">{metric.description}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
